import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import type { VerseOfWeek } from '../../types';

interface MaskedWord {
  index: number;
  word: string;
  userAnswer: string;
  isCorrect: boolean | null;
}

export default function VerseOfWeek() {
  const [verse, setVerse] = useState<VerseOfWeek | null>(null);
  const [loading, setLoading] = useState(true);
  const [maskedWords, setMaskedWords] = useState<MaskedWord[]>([]);
  const [showAnswers, setShowAnswers] = useState(false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    const fetchVerse = async () => {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      
      const { data: verseData } = await supabase
        .from('verses')
        .select('*')
        .eq('active', true)
        .lte('week_start', today)
        .order('week_start', { ascending: false })
        .limit(1)
        .single();
      
      setVerse(verseData);
      
      if (verseData) {
        const words = verseData.text.split(/\s+/);
        const wordsToMask = shuffleArray([...words.entries()])
          .slice(0, Math.min(4, Math.floor(words.length * 0.3)));
        
        setMaskedWords(wordsToMask.map(([index, word]) => ({
          index,
          word: word.replace(/[.,;!?]/g, ''),
          userAnswer: '',
          isCorrect: null,
        })));
      }
      
      setLoading(false);
    };
    
    fetchVerse();
  }, []);

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const renderMaskedText = useMemo(() => {
    if (!verse) return '';
    
    const words = verse.text.split(/\s+/);
    return words.map((word, idx) => {
      const masked = maskedWords.find(m => m.index === idx);
      if (masked) {
        if (showAnswers) {
          return (
            <span
              key={idx}
              className={masked.isCorrect ? 'text-green-300 font-bold' : 'text-red-300 font-bold'}
            >
              [{masked.word}]
            </span>
          );
        }
        return (
          <input
            key={idx}
            type="text"
            className="w-24 mx-1 px-2 py-1 text-center bg-white/20 border border-white/30 rounded text-white placeholder-white/50 focus:outline-none focus:border-white"
            placeholder="?"
            value={masked.userAnswer}
            onChange={(e) => handleAnswerChange(masked.index, e.target.value)}
          />
        );
      }
      return <span key={idx}>{word} </span>;
    });
  }, [verse, maskedWords, showAnswers]);

  const handleAnswerChange = (index: string | number, answer: string) => {
    setMaskedWords(prev => prev.map(m => 
      m.index === index ? { ...m, userAnswer: answer } : m
    ));
  };

  const handleCheckAnswers = () => {
    const newScore = maskedWords.filter(m => 
      m.userAnswer.toLowerCase().trim() === m.word.toLowerCase()
    ).length;
    
    setMaskedWords(prev => prev.map(m => ({
      ...m,
      isCorrect: m.userAnswer.toLowerCase().trim() === m.word.toLowerCase(),
    })));
    
    setScore(newScore);
    setShowAnswers(true);
  };

  const handleReset = () => {
    if (!verse) return;
    
    const words = verse.text.split(/\s+/);
    const wordsToMask = shuffleArray([...words.entries()])
      .slice(0, Math.min(4, Math.floor(words.length * 0.3)));
    
    setMaskedWords(wordsToMask.map(([index, word]) => ({
      index,
      word: word.replace(/[.,;!?]/g, ''),
      userAnswer: '',
      isCorrect: null,
    })));
    
    setShowAnswers(false);
    setScore(0);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen animate-pulse" style={{ backgroundColor: '#1C1C1E' }}>
        <div className="p-4 space-y-4">
          <div className="h-6 rounded" style={{ backgroundColor: '#2C2C2E', width: '60%' }} />
          <div className="rounded-lg p-6" style={{ backgroundColor: '#2C2C2E' }}>
            <div className="h-4 rounded mb-4" style={{ backgroundColor: '#3A3A3C', width: '40%' }} />
            <div className="space-y-2">
              <div className="h-5 rounded" style={{ backgroundColor: '#3A3A3C' }} />
              <div className="h-5 rounded" style={{ backgroundColor: '#3A3A3C', width: '80%' }} />
              <div className="h-5 rounded" style={{ backgroundColor: '#3A3A3C', width: '60%' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!verse) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1C1C1E' }}>
        <div className="text-center p-8">
          <div className="text-6xl mb-4">📖</div>
          <p className="text-white text-lg">No hay versículo para esta semana</p>
          <p className="text-sm mt-2" style={{ color: '#B3B3B3' }}>Vuelve pronto</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-4" style={{ backgroundColor: '#1C1C1E' }}>
      <div className="px-4 pt-2 pb-4">
        <h1 className="text-2xl font-bold text-white">Versículo de la Semana</h1>
        <p className="text-xs mt-0.5" style={{ color: '#8E8E93' }}>{formatDate(verse.week_start)}</p>
      </div>

      {/* Tarjeta versículo */}
      <div className="mx-4 rounded-lg p-6 mb-4" style={{ background: 'linear-gradient(135deg, #0D2B8E 0%, #2C2C2E 100%)' }}>
        <p className="text-lg leading-relaxed text-white mb-4">
          "{renderMaskedText}"
        </p>
        <p className="text-right font-semibold" style={{ color: '#4A90D9' }}>
          — {verse.reference}
        </p>
      </div>

      {/* Juego */}
      <div className="mx-4 rounded-lg p-4" style={{ backgroundColor: '#2C2C2E' }}>
        <h3 className="font-bold text-white mb-1 flex items-center gap-2">
          <span>🎯</span> Juego de Memorización
        </h3>
        <p className="text-sm mb-4" style={{ color: '#B3B3B3' }}>
          Completa las palabras que faltan
        </p>
        <div className="flex flex-wrap gap-3">
          {!showAnswers ? (
            <button
              onClick={handleCheckAnswers}
              className="flex-1 py-3 rounded-md font-bold text-white"
              style={{ backgroundColor: '#4A90D9' }}
            >
              Verificar
            </button>
          ) : (
            <>
              <div className="flex-1 py-3 rounded-md font-bold text-center" style={{ backgroundColor: '#3A3A3C', color: '#4A90D9' }}>
                {score}/{maskedWords.length} correctas
              </div>
              <button
                onClick={handleReset}
                className="py-3 px-5 rounded-md font-bold text-white"
                style={{ backgroundColor: '#3A3A3C' }}
              >
                Reintentar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
